import type { ZudokuConfig } from "zudoku";
import type { Reference } from "./src/reference/define";
import { Landing } from "./src/Landing";
import { HeaderLinks, TopNavMenus } from "./src/TopNavMenus";
import {
  HashUrlSync,
  referenceOverviews,
  referencePages,
} from "./src/reference/HashUrlSync";
import { referenceNavigation } from "./src/reference/navigation";
import { createPage } from "./src/reference/ReferencePage";
import { ApiOverview, apiReferences } from "./src/reference/api/plugin";
import { API_SOURCES } from "./src/reference/api/sources";
import { cartWidget } from "./src/reference/cart-widget/sections";
import { subblySdk } from "./src/reference/subbly-sdk/sections";
import { remarkCodeMeta } from "./src/remarkCodeMeta";
import { seo, SITE_URL } from "./src/seo";
import {
  Example,
  Method,
  Param,
  ParamGroup,
  Params,
  Properties,
  Returns,
} from "./src/JsReference";
import "./styles.css";

const jsReferences = [subblySdk, cartWidget];
const referencePaths = [...jsReferences, ...API_SOURCES];

// A page Zudoku routes and prerenders but keeps out of the tab bar. `file`
// is the page path without the leading slash, as Zudoku wants it.
const hiddenDoc = (path: string) => ({
  type: "doc" as const,
  file: path.replace(/^\//, ""),
  display: "hide" as const,
});

// One hidden `doc` entry per group page. The sidebar entries come from the
// navigation plugin instead.
const groupPages = (reference: Reference) =>
  reference.groups.map((group) => hiddenDoc(reference.pathOf(group)));

// An article whose sidebar label is shorter than its page title: the category
// already names the tool, so "Install" is enough where the page says "Install
// Subbly.js".
const article = (file: string, label: string) => ({
  type: "doc" as const,
  file,
  label,
});

const config: ZudokuConfig = {
  canonicalUrlOrigin: SITE_URL,
  sitemap: {
    siteUrl: SITE_URL,
    changefreq: "weekly",
    // No lastmod: Zudoku would stamp every URL with the build time, which
    // tells search engines nothing.
    autoLastmod: false,
  },
  metadata: {
    title: "%s | Subbly Developers",
    defaultTitle: "Subbly Developers",
    description: "Develop with Subbly's APIs and SDKs.",
    favicon: "/favicon.png",
  },
  theme: {
    // Palette and fonts follow subbly.dev. Dark mode reuses its "ink" sections.
    fonts: {
      sans: {
        url: "/fonts.css",
        fontFamily: '"Suisse Intl", Arial, "Helvetica Neue", Helvetica, sans-serif',
      },
      mono: "Roboto Mono",
    },
    light: {
      background: "#ffffff",
      foreground: "#0d1115",
      card: "#ffffff",
      cardForeground: "#0d1115",
      popover: "#ffffff",
      popoverForeground: "#0d1115",
      primary: "#2d5fff",
      primaryForeground: "#ffffff",
      secondary: "#f4f4f4",
      secondaryForeground: "#0d1115",
      muted: "#fafafa",
      mutedForeground: "#838587",
      accent: "#f4f4f4",
      accentForeground: "#0d1115",
      destructive: "#ef4444",
      destructiveForeground: "#ffffff",
      border: "rgb(13 17 21 / 0.1)",
      input: "rgb(13 17 21 / 0.15)",
      ring: "#2d5fff",
      radius: "0.25rem",
    },
    dark: {
      background: "#0d1115",
      foreground: "#ffffff",
      card: "#161b20",
      cardForeground: "#ffffff",
      popover: "#161b20",
      popoverForeground: "#ffffff",
      primary: "#2d5fff",
      primaryForeground: "#ffffff",
      secondary: "#161b20",
      secondaryForeground: "#ffffff",
      muted: "#161b20",
      mutedForeground: "#838587",
      accent: "#21272e",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      destructiveForeground: "#ffffff",
      border: "#3d4144",
      input: "#3d4144",
      ring: "#2d5fff",
      radius: "0.25rem",
    },
  },
  site: {
    showPoweredBy: false,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Subbly",
      width: "130px",
    },
  },
  docs: {
    // No previous/next links at the foot of a page. Each page ends with its
    // own "Next steps" where one fits.
    defaultOptions: { disablePager: true },
    // Build-time exports for LLMs: /llms.txt (index), /llms-full.txt (all
    // pages in one file) and a .md twin of every page.
    publishMarkdown: true,
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
      title: "Subbly Developers",
      description: "Develop with Subbly's APIs and SDKs.",
      instructions:
        "Use these docs to build or debug a Subbly integration. Start with the guides for concepts, then the API and SDK references for exact fields and methods.",
    },
  },
  navigation: [
    {
      type: "custom-page",
      path: "/",
      label: "Home",
      element: <Landing />,
      display: "hide",
    },
    {
      type: "category",
      label: "Get started",
      link: "get-started/overview",
      items: ["get-started/overview"],
    },
    {
      // Articles about the tools: install, configure, manage. The references
      // themselves live outside the tabs, like Stripe's API and JS references.
      type: "category",
      label: "Developer resources",
      link: "developer-resources/overview",
      // Articles only. The references are reached from the "References" menu
      // in the tab bar and from the links inside the articles. One open
      // category per tool, as the API references have one per tag.
      items: [
        "developer-resources/overview",
        {
          type: "category",
          label: "Subbly.js SDK",
          link: "developer-resources/subbly-js-sdk",
          collapsible: true,
          collapsed: false,
          items: [
            article("developer-resources/subbly-js-sdk/install", "Install"),
            article("developer-resources/subbly-js-sdk/configure", "Configure"),
            article(
              "developer-resources/subbly-js-sdk/keep-it-up-to-date",
              "Keep it up to date",
            ),
            "developer-resources/subbly-js-sdk/build-a-checkout",
            "developer-resources/subbly-js-sdk/handle-errors",
            "developer-resources/subbly-js-sdk/run-a-funnel",
          ],
        },
        {
          type: "category",
          label: "SubblyCart.js widget",
          link: "developer-resources/cart-widget",
          collapsible: true,
          collapsed: false,
          items: [
            article("developer-resources/cart-widget/install", "Install"),
            article("developer-resources/cart-widget/configure", "Configure"),
            article(
              "developer-resources/cart-widget/keep-it-up-to-date",
              "Keep it up to date",
            ),
            article(
              "developer-resources/cart-widget/wire-it-into-your-page",
              "Wire it into your page",
            ),
            article(
              "developer-resources/cart-widget/drive-it-from-your-code",
              "Drive it from your code",
            ),
            article("developer-resources/cart-widget/style-it", "Style it"),
          ],
        },
        {
          type: "category",
          label: "Subbly's MCP server",
          link: "developer-resources/subbly-mcp",
          collapsible: true,
          collapsed: false,
          items: [
            article(
              "developer-resources/subbly-mcp/connect-a-client",
              "Connect a client",
            ),
            article(
              "developer-resources/subbly-mcp/plans-and-limits",
              "Plans and limits",
            ),
            article("developer-resources/subbly-mcp/tools", "Tools"),
            article(
              "developer-resources/subbly-mcp/example-workflows",
              "Example workflows",
            ),
            article(
              "developer-resources/subbly-mcp/security-and-access",
              "Security and access",
            ),
            article(
              "developer-resources/subbly-mcp/troubleshooting",
              "Troubleshooting",
            ),
          ],
        },
      ],
    },
    // The references. Each page is routed here and hidden from the tab bar;
    // the sidebar of a reference comes from its plugin alone, so it holds
    // nothing but the reference. The API tag pages are routed by the
    // apiReferences plugin; only their overview pages are docs.
    ...jsReferences.flatMap((reference) => [
      hiddenDoc(reference.path),
      ...groupPages(reference),
    ]),
    ...API_SOURCES.map((api) => hiddenDoc(api.path)),
  ],
  redirects: [
    { from: "/api", to: "/api/storefront" },
    { from: "/guides/subbly-js-sdk", to: "/developer-resources/subbly-js-sdk" },
    { from: "/guides/cart-widget", to: "/developer-resources/cart-widget" },
    {
      from: "/developer-resources/run-a-funnel",
      to: "/developer-resources/subbly-js-sdk/run-a-funnel",
    },
  ],
  mdx: {
    components: {
      Method,
      Params,
      Param,
      ParamGroup,
      Properties,
      Returns,
      Example,
      CartWidgetPage: createPage(cartWidget),
      SdkPage: createPage(subblySdk),
      ApiOverview,
    },
  },
  plugins: [referenceNavigation(jsReferences), apiReferences(), seo()],
  build: { remarkPlugins: [remarkCodeMeta] },
  slots: {
    "top-navigation-side": <TopNavMenus />,
    "head-navigation-end": <HeaderLinks />,
    // Rendered inside <main> on every page; it acts on the reference pages.
    "content-after": (
      <HashUrlSync
        families={[
          referencePages(referencePaths),
          referenceOverviews(referencePaths),
        ]}
      />
    ),
  },
};

export default config;
